import { transliterate } from 'transliteration';

const STOP_PUNCTUATIONS = ['.', ',', '+', '&', '_', '-'];

// source: https://en.wikipedia.org/wiki/List_of_legal_entity_types_by_country
const STOP_LEGAL_ENTITY_TYPE_ACRONYMS = [
  // Common
  'ltd',
  'plc',
  'pvt',
  'lp',
  'gp',
  // Bulgaria
  'ead',
  'ood',
  // Croatia
  'dd',
  'doo',
  // Denmark
  'ivs',
  'aps',
  'a/s',
  // Estonia
  'ou',
  'as',
  // Finland
  'oy',
  'oyj',
  // France
  'sarl',
  'sa',
  'sas',
  // Germany
  'gmbh',
  'co',
  'kg',
  'ag',
  // Greece
  'ae',
  'ee',
  'epe',
  // Hungary
  'bt',
  'kft',
  'nyrt',
  // Iceland
  'ehf',
  'hf',
  'saf',
  // Italy
  'ss',
  'srl',
  'spa',
  // Latvia
  'sia',
  'ks',
  // Lithuania
  'uab',
  'ab',
  'tub',
  // Luxembourg
  'scs',
  // Netherlands
  'bv',
  'nv',
  // Norway
  'asa',
  'ans',
  // Poland
  'spk',
  'sp',
  'spzoo',
  'zoo',
  // Portugal
  'lda',
  // Slovakia
  'sro',
  // Slovenia
  'dno',
  // Spain
  'sl',
  'sc',
  // Sweden
  'kb',
  'hb',
  // Albania
  'shpk',
  'sha',
  'shk',
  // Australia
  'llp',
  'inc',
  'pty',
  // Brazil
  'sociedade',
  'limitada',
  'ltda',
  // Cambodia
  'pte',
  // Canada
  'limited',
  // Ecuador
  'cia',
  // Egypt
  'sae',
  // Malaysia
  'plt',
  'bhd',
  'sdn',
  // Turkey
  'san',
  'sanayi',
  've',
  'tic',
  'ticaret',
  'sti',
  'ls',
  'lo',
  // Vietnam
  'cty',
  'tnhh',
];

// Legal-entity forms that only appear *after* transliteration. `transliterate`
// turns 宁波联纺化纤有限公司 into "ning bo lian fang hua xian you xian gong si",
// so the suffix arrives as Latin words that the acronym list above — which is
// built from Latin-script abbreviations — has no entry for.
//
// Leaving them in is not cosmetic. Every Chinese name then ends in the same four
// tokens, which dominates both the trigram score and the embedding: measured on
// staging, 258 companies whose stored name is in Chinese characters produced
// 59,721 of 72,710 new entity-screening matches (82%), each averaging ~231
// spurious sanctions hits, purely on this shared boilerplate.
//
// Ordered longest-first: the patterns overlap ("gong si" is a suffix of all the
// others) and regex alternation takes the first branch that matches, so the
// specific form has to be offered before the generic one.
const STOP_TRANSLITERATED_LEGAL_FORMS = [
  // Chinese
  'gu fen you xian gong si', // 股份有限公司 — joint-stock limited company
  'you xian ze ren gong si', // 有限责任公司 — limited liability company
  'you xian gong si', // 有限公司 — limited company
  'fen gong si', // 分公司 — branch company
  'gong si', // 公司 — company
  // Vietnamese
  'cong ty co phan', // công ty cổ phần — joint-stock company
  'cong ty', // công ty — company
];

const REMOVE_PUNCTUATION_REGEX = new RegExp(
  `[${STOP_PUNCTUATIONS.join('')}]`,
  'gi',
);
// Tokens are joined with `\s+` rather than a literal space because punctuation
// is replaced by spaces just before this runs, so "Co.,Ltd" style input can
// leave runs of whitespace mid-phrase.
const REMOVE_TRANSLITERATED_LEGAL_FORMS_REGEX = new RegExp(
  STOP_TRANSLITERATED_LEGAL_FORMS.map(
    form => `\\b(${form.split(' ').join('\\s+')})\\b`,
  ).join('|'),
  'gi',
);
const REMOVE_STOP_WORDS_REGEX = new RegExp(
  STOP_LEGAL_ENTITY_TYPE_ACRONYMS.map(sw => `\\b(${sw})\\b`).join('|'),
  'gi',
);
const REMOVE_DUPLICATED_SPACES_REGEX = /[ ]+/gi;

const removeStopWords = (text: string): string =>
  text
    .toLowerCase()
    .replace(REMOVE_PUNCTUATION_REGEX, ' ')
    .replace(REMOVE_TRANSLITERATED_LEGAL_FORMS_REGEX, ' ')
    .replace(REMOVE_STOP_WORDS_REGEX, ' ')
    .replace(REMOVE_DUPLICATED_SPACES_REGEX, ' ')
    .trim();

/**
 * Ports the legacy `removeStopWords(transliterate(name))` preprocessing used
 * before the trigram similarity search — strips punctuation, common legal
 * entity suffixes (Ltd, GmbH, S.A., ...), and transliterates to latin script
 * so e.g. "Müller GmbH" and "Muller" score as similar.
 */
export const normalizeCompanyNameForSimilarity = (name: string): string =>
  removeStopWords(transliterate(name));

/**
 * The single rule for turning a name into the text that gets embedded —
 * normalize, and fall back to the raw name when normalization leaves nothing.
 *
 * Every side of every vector comparison must use this, because a vector is only
 * comparable to another vector built from text prepared the same way. Two
 * incidents came from that not holding:
 *
 * - `e420240a9` normalized the company *query* side and shipped no backfill, so
 *   stored company vectors stayed raw and an exact-name match scored 68 instead
 *   of 100.
 * - Entity-screening embeddings were built from raw entity names, so
 *   regenerating company vectors as normalized broke the pairing and dropped
 *   ~87% of vector-only sanctions matches below threshold.
 *
 * The fallback exists because a name made entirely of legal-entity stop words
 * (`CO & CO Srl`) normalizes to the empty string. Embedding nothing is not an
 * option — for screening in particular, no vector means a guaranteed miss — so
 * such a name keeps its raw form on every side, which stays self-consistent.
 */
export const normalizeNameForEmbedding = (
  name: string | null | undefined,
): string | null => {
  if (!name) return null;
  const normalized = normalizeCompanyNameForSimilarity(name);
  return normalized || name.trim() || null;
};
